import React, { useState, useEffect } from 'react';
import { Modal, Button, Checkbox, Space, Typography, Alert, Statistic, Row, Col, message } from 'antd';
import axios from 'axios';
import { API_URL } from '../config';

const { Text, Paragraph } = Typography;

interface SyncRoomDescriptionsModalProps {
  visible: boolean;
  hotelId: number;
  onClose: () => void;
  onSuccess: () => void;
}

interface PreviewStats {
  totalRooms: number;
  roomsWithExistingDescription: number;
  roomsWithoutRoomType: number;
  roomsWithRoomTypeMissingDescription: number;
  eligibleRooms: number;
}

const SyncRoomDescriptionsModal: React.FC<SyncRoomDescriptionsModalProps> = ({
  visible,
  hotelId,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [overwrite, setOverwrite] = useState(true);
  const [stats, setStats] = useState<PreviewStats | null>(null);

  useEffect(() => {
    if (visible && hotelId !== undefined) {
      loadPreview();
    } else if (!visible) {
      setStats(null);
      setOverwrite(true);
    }
  }, [visible, hotelId]);

  const loadPreview = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/rooms/sync-descriptions-preview`,
        { params: { hotelId } },
      );
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load preview:', error);
      message.error('Failed to load preview');
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/rooms/bulk-sync-descriptions-from-type`,
        { hotelId, overwrite },
      );

      const { updated, skipped, noRoomType, noRoomTypeDescription } = response.data;

      const parts = [`Updated ${updated}`];
      if (skipped > 0) parts.push(`skipped ${skipped} (already had descriptions)`);
      if (noRoomType > 0) parts.push(`${noRoomType} without matching room type`);
      if (noRoomTypeDescription > 0) parts.push(`${noRoomTypeDescription} with empty room-type description`);

      message.success(parts.join(', '));
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Sync error:', error);
      message.error(error.response?.data?.message || 'Failed to sync descriptions');
    } finally {
      setLoading(false);
    }
  };

  const affectedCount = stats
    ? overwrite
      ? stats.eligibleRooms
      : Math.max(0, stats.eligibleRooms - countOverlap(stats))
    : 0;

  function countOverlap(s: PreviewStats): number {
    return Math.min(s.roomsWithExistingDescription, s.eligibleRooms);
  }

  return (
    <Modal
      title="Sync Room Descriptions from Room Type"
      open={visible}
      onCancel={onClose}
      width={640}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
          disabled={!stats || stats.eligibleRooms === 0}
        >
          Sync Descriptions
        </Button>,
      ]}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Copies the description from each room's associated room type into the room's <Text code>descriptions</Text> field across all languages.
          Rooms without a matching room type, or whose room type has no description, are skipped.
        </Paragraph>

        {stats && (
          <>
            <Row gutter={16}>
              <Col span={8}>
                <Statistic title="Total Rooms" value={stats.totalRooms} />
              </Col>
              <Col span={8}>
                <Statistic title="Eligible" value={stats.eligibleRooms} valueStyle={{ color: '#3f8600' }} />
              </Col>
              <Col span={8}>
                <Statistic title="Already Have Description" value={stats.roomsWithExistingDescription} />
              </Col>
            </Row>

            {(stats.roomsWithoutRoomType > 0 || stats.roomsWithRoomTypeMissingDescription > 0) && (
              <Alert
                type="info"
                showIcon
                message="Some rooms will be skipped"
                description={
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {stats.roomsWithoutRoomType > 0 && (
                      <li>{stats.roomsWithoutRoomType} room(s) have no matching room type</li>
                    )}
                    {stats.roomsWithRoomTypeMissingDescription > 0 && (
                      <li>{stats.roomsWithRoomTypeMissingDescription} room(s) whose room type has no description</li>
                    )}
                  </ul>
                }
              />
            )}

            <Checkbox checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)}>
              Overwrite existing room descriptions
            </Checkbox>

            <Alert
              type={overwrite ? 'warning' : 'success'}
              showIcon
              message={
                overwrite
                  ? `${stats.eligibleRooms} room(s) will be updated. Existing descriptions will be overwritten.`
                  : `Up to ${affectedCount} room(s) will be updated. Rooms with existing descriptions will be kept.`
              }
            />
          </>
        )}
      </Space>
    </Modal>
  );
};

export default SyncRoomDescriptionsModal;
